import { useCallback, useState } from 'react';
import { Text, Flex, useToast, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { RosterMember } from '@/model';
import { RosterMemberFilters } from './types';
import { RosterMemberTable } from './components/RosterMemberTable/RosterMemberTable';
import { RosterMemberFilterBox } from './components/RosterMemberFilterBox';
import { RosterMemberCreateModal } from './components/RosterMemberCreateModal';
import { RosterMemberEditModal } from './components/RosterMemberEditModal';
import {
  useDeleteRosterMemberMutation,
  useGetRosterMemberById,
  useReorderRosterMembersMutation,
  useRosterMember,
} from '@/services/queries/roster-member';
import { useRosterGroup } from '@/services/queries/roster-group';
import { useAllClub } from '@/services/queries/club';

export const ApplicationRosterMember = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<RosterMember | undefined>(undefined);

  const { data: clubs = [] } = useAllClub(true);
  const deleteMutation = useDeleteRosterMemberMutation();
  const reorderMutation = useReorderRosterMembersMutation();
  const getItemByUUID = useGetRosterMemberById();

  const { data: groupsData } = useRosterGroup(
    GetParamsObject({}, 1, 100, 'asc', 'sort_order'),
  );
  const groups = groupsData?.data || [];

  const filtersForm = useFiltersForm<RosterMemberFilters>({
    defaultValues: {
      team_uuid: '',
      name: '',
      group_uuid: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting] = useState<{ field: string; desc: boolean }>({ field: 'sort_order', desc: false });
  const [pagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 100,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useRosterMember(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: RosterMember) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_roster_member.application_roster_member_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_roster_member.application_roster_member_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_roster_member.application_roster_member_edit_error'),
        });
      },
    });
  };

  const onHandleReorder = useCallback(
    (orderedUuids: string[]) => {
      reorderMutation.mutate(
        { items: orderedUuids.map((uuid, index) => ({ uuid, sort_order: index })) },
        {
          onSuccess: () => {
            forceRefetch();
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.application_roster_member.application_roster_member_reorder_error'),
            });
            forceRefetch();
          },
        },
      );
    },
    [reorderMutation, forceRefetch, toast, t],
  );

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  return (
    <PageContent title={t('pages.application_roster_member.application_roster_member_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'flex-end' }} gap={4}>
          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => setIsCreateModalOpen(true)}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_roster_member.application_roster_member_add_button')}
          </Button>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <RosterMemberFilterBox clubs={clubs} groups={groups} filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <RosterMemberTable
            data={itemsData}
            isLoading={isLoading}
            isReorderInFlight={reorderMutation.isLoading}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
            onReorder={onHandleReorder}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <RosterMemberEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          groups={groups}
          onDone={onHandleEditDone}
        />
      )}

      <RosterMemberCreateModal
        isOpen={!!isCreateModalOpen}
        groups={groups}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_roster_member.application_roster_member_delete_confirm_title')}
        description={
          <Text>
            {t('pages.application_roster_member.application_roster_member_delete_confirm_description_start')}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.name}"
            </Text>
            {t('pages.application_roster_member.application_roster_member_delete_confirm_description_end')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || deleteMutation.isLoading} />
    </PageContent>
  );
};
